import React, { useCallback, useEffect, useRef, useState } from 'react';
import { applicationsClient, devToolsClient } from 'api/AdgangClients';
import { store } from 'react-notifications-component';
import {
  Table,
  Th,
  Button,
  LoadingSpinner,
  useBoundForm,
  useFetch,
  usePaging,
  SelectOption,
  ConfirmationModal,
  SimpleModal,
  Form,
  Message
} from 'common.ui';
import {
  ApplicationResponse,
  AdgangAccessControl,
  GenericValidationError,
  ApplicationUpdateRequest,
  UserType,
  ApplicationResponsiblePersonRequest,
  ApplicationStatus,
  ApplicationMigrationStatus,
  ApplicationConsumptionResponse,
  ClientSecretResponse,
  PlatformType,
  ResponseError
} from 'api/adgang';
import RoutePaths from 'RoutePaths';
import { Link, useParams, useHistory } from 'react-router-dom';

import Page from 'components/page/Page';

import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { ReactComponent as IconDelete } from 'assets/icons/delete-icon.svg';
import { ReactComponent as IconCopyClipboard } from 'assets/icons/copy-clipboard-icon.svg';
import ApplicationResponsibleSelector from 'components/applications/ApplicationResponsibleSelector';
import { useUserAuthenticationConstraints } from 'api/hooks/useUserAuthenticationConstraint';
import { useApplicationResponsibles } from 'api/hooks/useApplicationResponsibles';
import { useUserTypes } from 'api/hooks/useUserTypes';
import { useAccessControlList } from 'hooks/access/useAccessControlList';

import { useApplicationStatuses } from 'api/hooks/useApplicationStatuses';
import { useApplicationMigrationStatuses } from 'api/hooks/useApplicationMigrationStatuses';

import './Application.scss';

type ApplicationFormModel = {
  userType: string;
} & Omit<ApplicationResponse, 'userType' | 'redirectUri'>;

type CreateSecretFormModel = {
  displayName: string;
  expires: string;
};

type AddRedirectUriFormModel = {
  redirectUri: string;
  type: PlatformType;
};

interface IParams {
  id: string;
}

function Application() {
  const { id } = useParams<IParams>();
  const history = useHistory();

  const [isUnlockingExternalApprovers, setIsUnlockingExternalApprovers] = useState<boolean>(false);
  const [apiErrors, setApiErrors] = useState<GenericValidationError>();
  const [confirmExternalApproversUnlocked, setConfirmExternalApproversUnlocked] = useState({
    externalApproversUnlocked: false,
    modalConfirmed: false
  });
  const [values, setValues] = useState<ApplicationFormModel>({} as ApplicationFormModel);
  const [resourceAcl, setResourceAcl] = useState<Array<AdgangAccessControl>>([]);
  const [, hasUserAccess] = useAccessControlList();
  const [userAuthenticationConstraints] = useUserAuthenticationConstraints(true, false);
  const [userTypes] = useUserTypes({ id: '', text: 'Interne og eksterne brukere' } as SelectOption);
  const [statuses] = useApplicationStatuses();
  const [migrationStatuses] = useApplicationMigrationStatuses();
  const [deleteApplication, setDeleteApplication] = useState<ApplicationConsumptionResponse>();
  const [deleteSecret, setDeleteSecret] = useState<string | undefined>();
  const [deletingSecret, setDeletingSecret] = useState<boolean>(false);
  const [deleteRedirectUri, setDeleteRedirectUri] = useState<string | undefined>();
  const [deletingRedirectUri, setDeletingRedirectUri] = useState<boolean>(false);
  const [pendingRemovedRedirectUris, setPendingRemovedRedirectUris] = useState<string[]>([]);
  const [pendingAddedRedirectUris, setPendingAddedRedirectUris] = useState<string[]>([]);
  const [creatingSecret, setCreatingSecret] = useState<boolean>(false);
  const [showCreateSecretModal, setShowCreateSecretModal] = useState<boolean>(false);
  const [createSecretApiErrors, setCreateSecretApiErrors] = useState<GenericValidationError>();
  const [temporarySecretText, setTemporarySecretText] = useState<string | undefined>();
  const [temporarySecretKeyId, setTemporarySecretKeyId] = useState<string | undefined>();
  const [pendingCreatedSecret, setPendingCreatedSecret] = useState<ClientSecretResponse | undefined>();
  const [pendingRemovedSecretKeyIds, setPendingRemovedSecretKeyIds] = useState<string[]>([]);
  const refreshPageTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [createSecretValues, setCreateSecretValues] = useState<CreateSecretFormModel>({
    displayName: '',
    expires: '6'
  });
  const [showAddRedirectUriModal, setShowAddRedirectUriModal] = useState<boolean>(false);
  const [addingRedirectUri, setAddingRedirectUri] = useState<boolean>(false);
  const [addRedirectUriApiErrors, setAddRedirectUriApiErrors] = useState<GenericValidationError>();
  const [addRedirectUriValues, setAddRedirectUriValues] = useState<AddRedirectUriFormModel>({
    redirectUri: '',
    type: PlatformType.Web
  });
  const [applicationResponsibles] = useApplicationResponsibles();
  const [mdirSkoleSyncResult, setMdirSkoleSyncResult] = useState<{
    show: boolean;
    loading: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data?: any;
    error?: string;
  }>({ show: false, loading: false });

  const fetchApplicationAndSetAcl = async (): Promise<ApplicationResponse> => {
    const app = await applicationsClient.getApplicationById({ id });
    setResourceAcl(app?.accessControls ?? []);
    return app;
  };

  const [application, isLoading, , fetchApplications] = useFetch<ApplicationResponse | undefined>(
    fetchApplicationAndSetAcl,
    undefined,
    false,
    [id]
  );

  const requestDelete = async () => {
    const c = await applicationsClient.apiApplicationsCheckusageGet({
      id
    });
    setDeleteApplication(c);
  };

  const requestDeleteSecret = async (keyId: string | undefined | null) => {
    setDeleteSecret(keyId ?? undefined);
  }

  const requestDeleteRedirectUri = (uri: string | undefined | null) => {
    setDeleteRedirectUri(uri ?? undefined);
  };

  const hasAccess = (accessControl: AdgangAccessControl): boolean => {
    return !!resourceAcl && resourceAcl.some((x) => x === accessControl);
  };

  const isReadOnly = !hasAccess(AdgangAccessControl.ApplicationEdit);

  // Nullstiller skjemaverdiene under render når applikasjonen er hentet på nytt, i stedet for i en effekt
  const [previousApplication, setPreviousApplication] = useState(application);
  if (previousApplication !== application) {
    setPreviousApplication(application);
    if (application) {
      setValues({
        ...application,
        userType: application.userType ? (application.userType as UserType) : ''
      });
    }
  }

  useEffect(() => {
    return () => {
      if (refreshPageTimeoutRef.current) {
        clearTimeout(refreshPageTimeoutRef.current);
      }
    };
  }, []);

  // Rydder ventende endringer under render når listene fra serveren endres, i stedet for i effekter
  const redirectUris = application?.redirectUris;
  const [previousRedirectUris, setPreviousRedirectUris] = useState(redirectUris);
  if (previousRedirectUris !== redirectUris) {
    setPreviousRedirectUris(redirectUris);
    if (redirectUris) {
      setPendingRemovedRedirectUris((prev) => prev.filter((uri) => redirectUris.includes(uri)));
      setPendingAddedRedirectUris((prev) => prev.filter((uri) => !redirectUris.includes(uri)));
    }
  }

  const passwordCredentials = application?.passwordCredentials;
  const [previousPasswordCredentials, setPreviousPasswordCredentials] = useState(passwordCredentials);
  if (previousPasswordCredentials !== passwordCredentials) {
    setPreviousPasswordCredentials(passwordCredentials);
    if (passwordCredentials) {
      const keyIds = passwordCredentials.map((s) => s.keyId).filter((k): k is string => !!k);
      setPendingRemovedSecretKeyIds((prev) => prev.filter((k) => keyIds.includes(k)));
      setPendingCreatedSecret((prev) => (prev?.keyId && keyIds.includes(prev.keyId) ? undefined : prev));
    }
  }

  usePaging(fetchApplications);

  const OnFormSubmit = async (submitModel: ApplicationFormModel) => {
    const model = {
      ...submitModel,
      canAppResponsiblesAppointExternalApprovers: values.canAppResponsiblesAppointExternalApprovers,
      responsiblePersons: values.responsiblePersons || []
    };
    setValues(model);
    try {
      await applicationsClient.apiApplicationsIdPut({
        id,
        applicationUpdateRequest: {
          ...model,
          isSearchableInMinSide: model.isSearchableInMinSide?.toString() === 'on',
          isIdPortenFederationDisabled: model.isIdPortenFederationDisabled?.toString() === 'on',
          isStafFederationDisabled: model.isStafFederationDisabled?.toString() === 'on',
          requiresElevatedSecurityPolicy: model.requiresElevatedSecurityPolicy?.toString() === 'on',
          requiresMfa: model.requiresMfa?.toString() === 'on',
          userType: model.userType ? (model.userType as UserType) : undefined,
          status: model.status ? (model.status as ApplicationStatus) : undefined,
          migrationStatus: model.migrationStatus
            ? (model.migrationStatus as ApplicationMigrationStatus)
            : undefined
        } as ApplicationUpdateRequest
      });
      setApiErrors(undefined);
      history.push(RoutePaths.applications);
    } catch (e) {
      if (e instanceof ResponseError) {
        const result = (await e.response.json()) as GenericValidationError;
        if (result) {
          setApiErrors(result);
        }
      }
    }
  };

  async function unlockApplicationForExternalApprovers() {
    if (application?.id) {
      setIsUnlockingExternalApprovers(true);
      await applicationsClient.apiApplicationsIdUnlockExternalApproverSupportPut({
        id: application.id
      });
      setConfirmExternalApproversUnlocked({
        modalConfirmed: true,
        externalApproversUnlocked: true
      });
      setIsUnlockingExternalApprovers(false);
    }
  }

  function renderNumber(result: JSX.Element[], num: number | undefined, text: string, pluralText: string) {
    const n = num ?? 0;
    if (n === 1) {
      result.push(<>1 {text}</>);
    } else if (n > 1) {
      result.push(
        <>
          {n} {pluralText}
        </>
      );
    }
  }

  const canApplicationBeUnlockedForExternalApprovers = () => {
    if (application) {
      // Application should allow external users
      if (application.userType == null || application.userType === UserType.External) {
        // User should be authorized and application should not already be unlocked
        if (
          hasAccess(AdgangAccessControl.ApplicationUnlockExternalApprover) &&
          !application.allowsExternalApprovers &&
          !confirmExternalApproversUnlocked.modalConfirmed
        ) {
          return true;
        }
      }
    }
    return false;
  };

  function renderUsage(r: ApplicationConsumptionResponse | undefined) {
    if (r === undefined || !r.isConsumedByOtherEntitities) return <></>;

    const result: JSX.Element[] = [];
    renderNumber(result, r.numRoles, 'rolle', 'roller');
    renderNumber(result, r.numPermissions, 'tilgang', 'tilganger');
    renderNumber(result, r.userMigrations, 'koblet konto', 'koblede kontoer');
    renderNumber(result, r.applicationResponsibles, 'applikasjonsansvarlig', 'applikasjonsansvarlige');

    return (
      <>
        <br />
        <span style={{ background: '#faf8dd' }}>
          ! Applikasjonen brukes av:
          {result.map((ro, i) => (
            <>
              {i === 0 ? ' ' : ', '}
              {ro}
            </>
          ))}
        </span>
      </>
    );
  }

  const { form, FormContainer, Checkbox, Dropdown, Input, DisplayErrors, TextArea } = useBoundForm<
    ApplicationFormModel
  >({
    onSubmit: async (e) => {
      await OnFormSubmit(e);
    },
    errors: apiErrors,
    model: values
  });

  const isDisabled =
    isUnlockingExternalApprovers || isReadOnly || isLoading || form.isLoading || !applicationResponsibles;

  const onResponsiblesChanged = useCallback((p: ApplicationResponsiblePersonRequest[]): void => {
    setValues((currentState) => ({ ...currentState, responsiblePersons: p }));
  }, []);

  const handleAccessRightSetChanged = (isExtended: boolean) => {
    setValues((currentState) => ({
      ...currentState,
      canAppResponsiblesAppointExternalApprovers: isExtended
    }));
  };

  const syncMdirSkoleUsers = async () => {
    setMdirSkoleSyncResult({ show: true, loading: true });
    try {
      const response = await devToolsClient.apiDevToolsMdirskoleSyncExternalUsersPostRaw();
      const result = await response.raw.json();
      setMdirSkoleSyncResult({
        show: true,
        loading: false,
        data: result
      });
    } catch (error) {
      setMdirSkoleSyncResult({
        show: true,
        loading: false,
        error: error instanceof Error ? error.message : 'Ukjent feil ved synkronisering'
      });
    }
  };

  const isMdirSkoleApp = application?.shortName?.toLowerCase() === 'mdirskole';

  const copySecretToClipboard = async (secretText: string) => {
    if (!secretText || !navigator.clipboard) {
      return;
    }

    await navigator.clipboard.writeText(secretText);

    store.addNotification({
      title: 'Kopiert',
      message: 'Secreten er kopiert til utklippstavlen',
      type: 'success',
      insert: 'top',
      container: 'top-right',
      animationIn: ['animate__animated', 'animate__fadeIn'],
      animationOut: ['animate__animated', 'animate__fadeOut'],
      dismiss: {
        duration: 10000
      }
    });
  };

  const secretDurationOptions: SelectOption[] = [
    { id: '6', text: '6 måneder' },
    { id: '12', text: '12 måneder' },
    { id: '24', text: '24 måneder' }
  ];

  const redirectUriTypeOptions: SelectOption[] = [
    { id: PlatformType.Web, text: 'Web' },
    { id: PlatformType.Spa, text: 'SPA' }
  ];

  const displayedSecrets: ClientSecretResponse[] = application?.passwordCredentials
    ? [
        ...application.passwordCredentials.filter(
          (s) => !s.keyId || !pendingRemovedSecretKeyIds.includes(s.keyId)
        ),
        ...(pendingCreatedSecret &&
        !application.passwordCredentials.some((s) => s.keyId === pendingCreatedSecret.keyId)
          ? [pendingCreatedSecret]
          : [])
      ]
    : [];

  const {
    form: createSecretForm,
    FormContainer: CreateSecretFormContainer,
    Input: CreateSecretInput,
    Dropdown: CreateSecretDropdown
  } = useBoundForm<CreateSecretFormModel>({
    onSubmit: async (secretModel) => {
      if (!application?.id) {
        return;
      }

      setCreateSecretValues(secretModel);
      setCreateSecretApiErrors(undefined);
      setCreatingSecret(true);

      if (displayedSecrets.length >= 2) {
        store.addNotification({
          title: 'Advarsel',
          message: 'Du kan kun ha to secrets samtidig. Vennligst slett en eksisterende secret før du oppretter en ny.',
          type: 'danger',
          insert: 'top',
          container: 'top-right',
          animationIn: ['animate__animated', 'animate__fadeIn'],
          animationOut: ['animate__animated', 'animate__fadeOut'],
          dismiss: {
            duration: 10000
          }
        });
        setCreatingSecret(false);
        setShowCreateSecretModal(false);
        return;
      }

      try {
        const createdSecret = await applicationsClient.apiApplicationsApplicationIdApplicationCredentialPost({
          applicationId: application.id,
          applicationCreateSecretRequest: {
            applicationId: application.id,
            displayName: secretModel.displayName,
            expires: Number(secretModel.expires)
          }
        });

        if (createdSecret.keyId && createdSecret.secretText) {
          setTemporarySecretKeyId(createdSecret.keyId as string);
          setTemporarySecretText(createdSecret.secretText as string);
          setPendingCreatedSecret({
            ...createdSecret,
            displayName: createdSecret.displayName ?? secretModel.displayName
          });

          if (refreshPageTimeoutRef.current) {
            clearTimeout(refreshPageTimeoutRef.current);
          }

          refreshPageTimeoutRef.current = setTimeout(() => {
            window.location.reload();
          }, 60 * 1000);
        }

        setShowCreateSecretModal(false);
        await fetchApplications();
      } catch (e) {
        if (e instanceof ResponseError && e.response.status === 400) {
          const apiError = (await e.response.json()) as GenericValidationError;
          if (apiError) {
            setCreateSecretApiErrors(apiError);
          }
        }
      } finally {
        setCreatingSecret(false);
      }
    },
    model: createSecretValues,
    errors: createSecretApiErrors

  });

  const {
    form: addRedirectUriForm,
    FormContainer: AddRedirectUriFormContainer,
    Input: AddRedirectUriInput,
    Dropdown: AddRedirectUriDropdown,
    DisplayErrors: AddRedirectUriDisplayErrors
  } = useBoundForm<AddRedirectUriFormModel>({
    onSubmit: async (redirectUriModel) => {
      if (!application?.id) {
        return;
      }

      setAddRedirectUriValues(redirectUriModel);
      setAddRedirectUriApiErrors(undefined);
      setAddingRedirectUri(true);

      try {
        await applicationsClient.apiApplicationsApplicationIdRedirecturiPost({
          applicationId: application.id,
          applicationAddRedirectUriRequest: {
            applicationId: application.id,
            redirectUri: redirectUriModel.redirectUri,
            platformType: redirectUriModel.type as PlatformType
          }
        });

        const addedUri = redirectUriModel.redirectUri;
        setPendingAddedRedirectUris((prev) =>
          prev.includes(addedUri) ? prev : [...prev, addedUri]
        );
        setShowAddRedirectUriModal(false);
        await fetchApplications();
      } catch (e) {
        if (e instanceof ResponseError && e.response.status === 400) {
          const apiError = (await e.response.json()) as GenericValidationError;
          if (apiError) {
            setAddRedirectUriApiErrors(apiError);
          }
        }
      } finally {
        setAddingRedirectUri(false);
      }
    },
    model: addRedirectUriValues,
    errors: addRedirectUriApiErrors
  });

  return (
    <Page header='Applikasjon' title={['Applikasjoner', application?.name || '']}>
      {isLoading || form.isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          {deleteApplication && application && !deleteApplication.isConsumedByOtherEntitities && (
            <DeleteApplicationModal
              application={application}
              form={form}
              onCancelled={() => setDeleteApplication(undefined)}
              onDeleted={() => {
                setDeleteApplication(undefined);
                history.push(RoutePaths.applications);
              }}
            />
          )}
          {deleteApplication && application && deleteApplication.isConsumedByOtherEntitities && (
            <SimpleModal show header='Slett applikasjon' onCancel={() => setDeleteApplication(undefined)}>
              Applikasjonen kan ikke slettes.
              {renderUsage(deleteApplication)}
            </SimpleModal>
          )}
          {deleteSecret && application && (
            <SimpleModal
              header='Slett secret'
              show
              onCancel={() => setDeleteSecret(undefined)}
            >
              Er du sikker på at du vil slette denne secreten? Denne handlingen kan ikke angres.
              <br />
              <br />
              <Button
                type='button'
                text='Slett'
                loading={deletingSecret}
                onClick={async () => {
                  if (deleteSecret) {
                    setDeletingSecret(true);
                      if (!application.id) {
                        setDeletingSecret(false);
                        return;
                      }
                      await applicationsClient.apiApplicationsApplicationIdApplicationCredentialDelete({
                        applicationId: application.id,
                        applicationRemoveSecretRequest: {
                            applicationId: application.id,
                            keyId: deleteSecret,
                        }
                      }).then(async () => {
                      setDeletingSecret(false);
                      const removedKeyId = deleteSecret;
                      setPendingRemovedSecretKeyIds((prev) =>
                        prev.includes(removedKeyId) ? prev : [...prev, removedKeyId]
                      );
                      if (deleteSecret && deleteSecret === temporarySecretKeyId) {
                        setTemporarySecretKeyId(undefined);
                        setTemporarySecretText(undefined);
                      }
                      if (pendingCreatedSecret?.keyId === deleteSecret) {
                        setPendingCreatedSecret(undefined);
                      }
                      setDeleteSecret(undefined);
                      await fetchApplications();
                    });
                }}
              }
              />
            </SimpleModal>
          )}
          {deleteRedirectUri && application && (
            <SimpleModal
              header='Slett redirect URI'
              show
              onCancel={() => setDeleteRedirectUri(undefined)}
            >
              Er du sikker på at du vil slette denne redirect URI-en? Denne handlingen kan ikke angres.
              <br />
              <br />
              <Button
                type='button'
                text='Slett'
                loading={deletingRedirectUri}
                onClick={async () => {
                  if (deleteRedirectUri) {
                    setDeletingRedirectUri(true);
                    if (!application.id) {
                      setDeletingRedirectUri(false);
                      return;
                    }
                    await applicationsClient.apiApplicationsApplicationIdRedirecturiDelete({
                      applicationId: application.id,
                      applicationRemoveRedirectUriRequest: {
                        applicationId: application.id,
                        redirectUri: deleteRedirectUri
                      }
                    }).then(async () => {
                      const removedUri = deleteRedirectUri;
                      setPendingRemovedRedirectUris((prev) =>
                        prev.includes(removedUri) ? prev : [...prev, removedUri]
                      );
                      setDeletingRedirectUri(false);
                      setDeleteRedirectUri(undefined);
                      await fetchApplications();
                    });
                  }
                }}
              />
            </SimpleModal>
          )}
          {showCreateSecretModal && application && (
            <SimpleModal
              header='Opprett ny secret'
              show
              onCancel={() => {
                if (!creatingSecret) {
                  setShowCreateSecretModal(false);
                }
              }}
            >
              <CreateSecretFormContainer form={createSecretForm}>
                <Row>
                  <Col sm={12} lg={12}>
                    <CreateSecretInput
                      form={createSecretForm}
                      name='displayName'
                      label='Navn på secret'
                      readonly={creatingSecret}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12} lg={12}>
                    <CreateSecretDropdown
                      form={createSecretForm}
                      name='expires'
                      label='Gyldighetstid'
                      options={secretDurationOptions}
                      readonly={creatingSecret}
                    />
                  </Col>
                </Row>
                <Row>
                  <Button type='submit' text='Opprett' loading={creatingSecret} disabled={creatingSecret} />
                  <Button
                    type='button'
                    text='Avbryt'
                    styleType='light'
                    disabled={creatingSecret}
                    onClick={() => setShowCreateSecretModal(false)}
                  />
                </Row>
              </CreateSecretFormContainer>
            </SimpleModal>
          )}
          {showAddRedirectUriModal && application && (
            <SimpleModal
              header='Legg til redirect URI'
              show
              onCancel={() => {
                if (!addingRedirectUri) {
                  setShowAddRedirectUriModal(false);
                }
              }}
            >
              <AddRedirectUriFormContainer form={addRedirectUriForm}>
                <Row>
                  <Col sm={12} lg={12}>
                    <AddRedirectUriDisplayErrors form={addRedirectUriForm} />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12} lg={12}>
                    <AddRedirectUriInput
                      form={addRedirectUriForm}
                      name='redirectUri'
                      label='Redirect URI'
                      readonly={addingRedirectUri}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col sm={12} lg={12}>
                    <AddRedirectUriDropdown
                      form={addRedirectUriForm}
                      name='type'
                      label='Type'
                      options={redirectUriTypeOptions}
                      readonly={addingRedirectUri}
                    />
                  </Col>
                </Row>
                <Row>
                  <Button type='submit' text='Legg til' loading={addingRedirectUri} disabled={addingRedirectUri} />
                  <Button
                    type='button'
                    text='Avbryt'
                    styleType='light'
                    disabled={addingRedirectUri}
                    onClick={() => setShowAddRedirectUriModal(false)}
                  />
                </Row>
              </AddRedirectUriFormContainer>
            </SimpleModal>
          )}
          {application && confirmExternalApproversUnlocked.externalApproversUnlocked && (
            <SimpleModal
              show
              header='Applikasjon er nå forberedt for bruk av eksterne godkjennere'
              onCancel={() =>
                setConfirmExternalApproversUnlocked({
                  modalConfirmed: true,
                  externalApproversUnlocked: false
                })
              }
            >
              Applikasjonen tillater nå at det opprettes eksterne godkjennere for de eksterne organisasjoner
              (brukersted) som har behov for dette. Kravet for å benytte denne muligheten er at rettigheten
              (rollen /tilgangen) som tildeles ekstern godkjenner (etter søknad) er satt opp med et krav om at
              godkjenner velger den organisasjon (fra en liste) som de skal representere. Dette gjelder videre
              også de eksterne rettigheter som sluttbruker søker om til den eksterne godkjenneren. Ekstern
              godkjenning vil kun gjelde rettighetsforespørsler fra brukere som har samme arbeidsgiver
              (organisasjonsnummer) som godkjenneren har eller som representerer samme organisasjon (fra
              liste).
            </SimpleModal>
          )}
          {mdirSkoleSyncResult.show && (
            <SimpleModal
              show
              header='MdirSkole Brukersynkronisering'
              onCancel={() => setMdirSkoleSyncResult({ show: false, loading: false })}
            >
              {mdirSkoleSyncResult.loading && <LoadingSpinner />}
              {mdirSkoleSyncResult.error && <Message text={mdirSkoleSyncResult.error} type='error' />}
              {mdirSkoleSyncResult.data && (
                <div>
                  <p>
                    <strong>Synkronisering fullført</strong>
                  </p>
                  <Table>
                    <tbody>
                      <tr>
                        <td>Totalt antall brukere:</td>
                        <td>{mdirSkoleSyncResult.data.totalUsers || 0}</td>
                      </tr>
                      <tr>
                        <td>Oppdaterte brukere:</td>
                        <td>{mdirSkoleSyncResult.data.updatedUsers || 0}</td>
                      </tr>
                      <tr>
                        <td>Allerede synkronisert:</td>
                        <td>{mdirSkoleSyncResult.data.alreadyInSync || 0}</td>
                      </tr>
                      <tr>
                        <td>Ikke funnet i MdirSkole:</td>
                        <td>{mdirSkoleSyncResult.data.notFoundInMdirSkole || 0}</td>
                      </tr>
                      <tr>
                        <td>Feil:</td>
                        <td>{mdirSkoleSyncResult.data.errorCount || 0}</td>
                      </tr>
                    </tbody>
                  </Table>
                  {mdirSkoleSyncResult.data.errors && mdirSkoleSyncResult.data.errors.length > 0 && (
                    <div style={{ marginTop: '1rem' }}>
                      <strong>Feilmeldinger:</strong>
                      <ul>
                        {mdirSkoleSyncResult.data.errors.map((err: string, idx: number) => (
                          <li key={idx}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </SimpleModal>
          )}

          <Container fluid>
            <FormContainer form={form}>
              <Row>
                <Col sm={12} lg={6}>
                  <DisplayErrors form={form} />
                </Col>
              </Row>
              <Row>
                <Col sm={12} lg={6}>
                  <Input form={form} name='name' label='Navn' readonly={isReadOnly} />
                </Col>
              </Row>
              <Row>
                <Col sm={12} lg={6}>
                  <Input form={form} name='shortName' label='Kortnavn' readonly={isReadOnly} />
                </Col>
              </Row>
              <Row>
                <Col sm={12} lg={6}>
                  <TextArea
                    form={form}
                    name='description'
                    label='Beskrivelse'
                    placeholder='Beskrivelse - Vises blant annet i MinSide'
                    readonly={isReadOnly}
                  />
                </Col>
              </Row>
              <Row>
                <Col sm={12} lg={6}>
                  <Input form={form} name='url' label='Url' readonly={isReadOnly} />
                </Col>
              </Row>
              <Row>
                <Col sm={12} lg={6}>
                  <Dropdown
                    form={form}
                    name='status'
                    label='Applikasjon status'
                    options={statuses}
                    readonly={isReadOnly}
                  />
                </Col>
              </Row>
              <Row>
                <Col sm={12} lg={6}>
                  <Dropdown
                    form={form}
                    name='migrationStatus'
                    label='Er det mulig å overføre rettigheter fra gammel til ny løsning nå?'
                    options={migrationStatuses}
                    readonly={isReadOnly}
                  />
                </Col>
              </Row>
              <Row>
                <Col sm={12} lg={6}>
                  <Dropdown
                    form={form}
                    name='userType'
                    label='Ment for type bruker'
                    options={userTypes}
                    readonly={isReadOnly}
                  />
                </Col>
              </Row>
              <Row>
                <Col sm={12} lg={6}>
                  <Dropdown
                    form={form}
                    name='userAuthenticationConstraint'
                    label='Type brukerautentisering'
                    options={userAuthenticationConstraints}
                    readonly={isReadOnly}
                  />
                </Col>
              </Row>
              <Row>
                <Col sm={12} lg={6}>
                  <Checkbox
                    form={form}
                    name='isIdPortenFederationDisabled'
                    label='Deaktiver ID Porten pålogging for denne applikasjonen?'
                    question=''
                    disabled={isReadOnly}
                  />
                </Col>
              </Row>
              <Row>
                <Col sm={12} lg={6}>
                  <Checkbox
                    form={form}
                    name='isStafFederationDisabled'
                    label='Deaktiver Statsforvalteren pålogging for denne applikasjonen?'
                    question=''
                    disabled={isReadOnly}
                  />
                </Col>
              </Row>
              <Row>
                <Col sm={12} lg={6}>
                  <Checkbox
                    form={form}
                    name='isSearchableInMinSide'
                    label='Er applikasjonen søkbar i MinSide?'
                    question=''
                    disabled={isReadOnly}
                  />
                </Col>
              </Row>
              <Row>
                <Col sm={12} lg={6}>
                  <Checkbox
                    form={form}
                    name='requiresMfa'
                    label='Krever applikasjonen multi-faktor autentisering for eksterne brukere?'
                    question=''
                    disabled={isReadOnly}
                  />
                </Col>
              </Row>
              <Row>
                <Col sm={12} lg={6}>
                  <Checkbox
                    form={form}
                    name='requiresElevatedSecurityPolicy'
                    label='Krever applikasjonen påloggingsflyten med et høyere sikkerhetsnivå (gjelder eksterne brukere)?'
                    question=''
                    disabled={isReadOnly}
                  />
                </Col>
              </Row>
              {hasAccess(AdgangAccessControl.ApplicationEditViewResponsibles) && (
                <Row>
                  <Col sm={12} lg={6}>
                    <h2>Applikasjonsansvarlig</h2>
                    <ApplicationResponsibleSelector
                      readonly={
                        isDisabled || !hasAccess(AdgangAccessControl.ApplicationEditChangeResponsibles)
                      }
                      selectOptions={applicationResponsibles}
                      responsiblePersons={application?.responsiblePersons ?? []}
                      expandedAccessRightsSet={values?.canAppResponsiblesAppointExternalApprovers ?? false}
                      showAccessRightsExpander={hasAccess(
                        AdgangAccessControl.ApplicationExtendAppResponsibleAccessRights
                      )}
                      onAccessRightSetChanged={handleAccessRightSetChanged}
                      onChanged={onResponsiblesChanged}
                    />
                  </Col>
                </Row>
              )}
              <Row>
                <Col sm={12} lg={6}>
                  {canApplicationBeUnlockedForExternalApprovers() && (
                    <Button
                      type='button'
                      text='Lås opp eksterne godkjennere'
                      onClick={unlockApplicationForExternalApprovers}
                      disabled={isDisabled}
                    />
                  )}
                  {isMdirSkoleApp && hasUserAccess(AdgangAccessControl.HangfireDashboardView) && (
                    <Button
                      type='button'
                      text='Synkroniser MdirSkole-brukere'
                      onClick={syncMdirSkoleUsers}
                      disabled={isDisabled || mdirSkoleSyncResult.loading}
                      styleType='light'
                    />
                  )}
                </Col>
              </Row>
              {application?.externalApprovers && application.externalApprovers.length > 0 && (
                <Row>
                  <Col sm={12} lg={6}>
                    <h2>Eksterne godkjennere</h2>

                    <Table>
                      <thead>
                        <tr>
                          <Th title='Navn' />
                        </tr>
                      </thead>
                      <tbody>
                        {application?.externalApprovers?.map((e) => (
                          <tr key={e.userId ?? ''}>
                            <td>
                              <Link to={`${RoutePaths.externalusers}/${e.userId}`}>
                                {e.firstName} {e.surname}
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Col>
                </Row>
              )}
              <Row>
                <Col sm={12} lg={6}>
                  <h2>Teknisk informasjon</h2>

                  <Table>
                    <thead>
                      <tr>
                        <Th colSpan={2} title='Tekniske detaljer' />
                      </tr>
                    </thead>
                    <tbody>
                      <tr key='clientId'>
                        <td>Client ID</td>
                        <td>{application?.clientId}</td>
                      </tr>
                    </tbody>
                  </Table>
                </Col>
              </Row>
              <Row>
                <Col sm={12} lg={6}>
                  {application?.redirectUris && (
                    <Table>
                      <thead>
                        <tr>
                          <Th title='Redirect Uri' />
                          {hasAccess(AdgangAccessControl.ApplicationEdit) && <Th title='Fjern' />}
                        </tr>
                      </thead>
                      <tbody>
                        {[
                          ...application.redirectUris.filter(
                            (r) => !pendingRemovedRedirectUris.includes(r)
                          ),
                          ...pendingAddedRedirectUris.filter(
                            (r) => !application.redirectUris!.includes(r)
                          )
                        ].map((r) => (
                          <tr key={r}>
                            <td>{r}</td>
                            {hasAccess(AdgangAccessControl.ApplicationEdit) && (
                              <td>
                                <IconDelete
                                  className='copy-clipboard-button'
                                  onClick={() => requestDeleteRedirectUri(r)}
                                />
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Col>
              </Row>
              <Row>
                <Col sm={12} lg={6}>
                  {application?.passwordCredentials && hasAccess(AdgangAccessControl.ApplicationEdit) && (
                    <Table>
                      <thead>
                        <tr>
                          <Th title='Secrets' />
                          <Th title='' />
                          <Th title='' />
                          <Th title='' />
                        </tr>
                      </thead>
                      <thead className='table-head-secondary'>
                        <tr>
                          <Th title='Navn' />
                          <Th title='Hint' />
                          <Th title='Utgår' />
                          <Th title='Fjern' />
                        </tr>
                      </thead>
                      <tbody>
                        {displayedSecrets.map((psc: ClientSecretResponse, idx: number) => {
                          const currentTemporarySecretText =
                            psc.keyId && psc.keyId === temporarySecretKeyId
                              ? temporarySecretText
                              : undefined;

                          return (
                            <tr
                              key={psc.keyId ?? `${psc.displayName ?? 'password-credential'}-${idx}`}
                              className={psc.endsOn && new Date(psc.endsOn) < new Date() ? 'expired-secret' : ''}
                            >
                              <td>{psc.displayName}</td>
                              <td>
                                {currentTemporarySecretText || psc.hint?.concat('******************')}
                                {currentTemporarySecretText && (
                                  <IconCopyClipboard
                                    className='copy-clipboard-button'
                                    style={{ marginLeft: '0.5rem' }}
                                    onClick={() => copySecretToClipboard(currentTemporarySecretText)}
                                  />
                                )}
                              </td>
                              <td>{psc.endsOn?.toLocaleDateString('no-NO')}</td>
                              <td>
                                <IconDelete className='copy-clipboard-button' onClick={() => requestDeleteSecret(psc.keyId)} />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  )}
                </Col>
              </Row>

              <Row>
                <Button type='submit' text='Lagre' disabled={isDisabled} />
                <Button
                  type='button'
                  text='Avbryt'
                  styleType='light'
                  disabled={isDisabled}
                  onClick={() => history.push(RoutePaths.applications)}
                />
                <Button
                  type='button'
                  text='Opprett ny secret'
                  styleType='light'
                  disabled={isDisabled || !hasAccess(AdgangAccessControl.ApplicationEdit)}
                  onClick={() => {
                    setCreateSecretApiErrors(undefined);
                    setCreateSecretValues({ displayName: '', expires: '6' });
                    setShowCreateSecretModal(true);
                  }}
                />
                <Button
                  type='button'
                  text='Legg til redirect URI'
                  styleType='light'
                  disabled={isDisabled || !hasAccess(AdgangAccessControl.ApplicationEdit)}
                  onClick={() => {
                    setAddRedirectUriApiErrors(undefined);
                    setAddRedirectUriValues({ redirectUri: '', type: PlatformType.Web });
                    setShowAddRedirectUriModal(true);
                  }}
                />
              </Row>
              <Row>
                {hasAccess(AdgangAccessControl.ApplicationDelete) && (
                  <Button
                    type='button'
                    text='Slett applikasjonen'
                    disabled={isDisabled || !hasAccess(AdgangAccessControl.ApplicationDelete)}
                    onClick={requestDelete}
                    styleType='light'
                  />
                )}
              </Row>

            </FormContainer>
          </Container>
        </>
      )}
    </Page>
  );
}

function DeleteApplicationModal({
  application,
  form,
  onCancelled,
  onDeleted
}: {
  application: ApplicationResponse;
  form: Form<ApplicationFormModel>;
  onCancelled: () => void;
  onDeleted: () => void;
}) {
  const [error, setError] = useState<GenericValidationError | undefined>();
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

  async function onDeleteConfirmed() {
    if (application?.id) {
      setDeleteLoading(true);
      let isError = false;
      try {
        setError(undefined);
        await applicationsClient.apiApplicationsIdDelete({
          id: application.id
        });
      } catch (e) {
        isError = true;
        if (e instanceof ResponseError && e.response.status === 400) {
          const apiError = (await e.response.json()) as GenericValidationError;
          if (apiError) {
            setError(apiError);
          }
        }
      } finally {
        setDeleteLoading(false);

        if (!isError) onDeleted();
      }
    }
  }

  return (
    <ConfirmationModal
      header='Slett applikasjon'
      show={application !== undefined}
      onCancel={onCancelled}
      onAccept={onDeleteConfirmed}
      disabled={error !== undefined || form.isLoading}
      hideButtons={deleteLoading}
    >
      {!error && 'Er du sikker på at du vil slett denne applikasjonen?'}
      {deleteLoading && <LoadingSpinner />}
      {error?.messages && error.messages.length > 0 && (
        <Message key='app_delete_err' text={error.messages[0]} type='error' />
      )}
    </ConfirmationModal>
  );
}

export default Application;
